import {useLazyQuery} from "@apollo/client";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import union from "lodash/union";
import pluralize from "pluralize";
import {useEffect, useMemo, useState} from "react";
import {FiTool} from "react-icons/fi";
import {useDebounce} from "use-debounce";

import {Button} from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {MatchMode} from "@/components/filterDropdown/FilterDropdown.ts";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {ListPanelSelect, ListPanelSelectOption} from "@/components/listPanelSelect/ListPanelSelect.tsx";
import {MatchModeToggle} from "@/components/matchModeToggle/MatchModeToggle.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {TagSelect} from "@/components/TagSelect/TagSelect.tsx";
import {GQL_MAINTENANCE_SCHEDULES_LIST, MaintenanceListResponse} from "@/lib/queries/maintenance.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {MaintenanceScheduleState, MaintenanceScheduleType} from "@/lib/types/maintenance.ts";
import {
  getNextOccurrenceText,
  getScheduleState,
} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/MaintenanceSchedulesTable.utils.ts";
import {getRecurrenceDisplay} from "@/pages/maintenance/maintenance.utils.ts";

import styles from "./MultiMaintenanceScheduleSelect.module.scss";

export interface MultiMaintenanceScheduleSelectProps {
  skipRequest?: boolean;
  className?: string;
  defaultValue?: number[];
  excludeIds?: number[];
  onAppend: (value: number) => void;
  onReplace: (values: number[]) => void;
  tags: ServiceTag[];
  variant?: "default" | "short";
}

export const MultiMaintenanceScheduleSelect = ({
  className,
  tags,
  defaultValue,
  skipRequest,
  excludeIds,
  onAppend,
  onReplace,
  variant = "default",
}: MultiMaintenanceScheduleSelectProps) => {
  const [searchSchedules, {data, loading, refetch}] =
    useLazyQuery<MaintenanceListResponse>(GQL_MAINTENANCE_SCHEDULES_LIST);
  const [selectedTags, setSelectedTags] = useState<readonly ServiceTag[]>([]);
  const [tagsMatch, setTagsMatch] = useState<MatchMode>("OR");
  const [search, setSearch] = useState("");
  const [searchValue] = useDebounce(search, 300);
  const [selectedSchedules, setSelectedSchedules] = useState<number[]>(defaultValue || []);
  const [availableSchedules, setAvailableSchedules] = useState<number[]>([]);

  const scheduleFilters = useMemo(
    () => ({
      tags: selectedTags.map((t) => t.id.toString()),
      tags_match: tagsMatch,
      types: [MaintenanceScheduleType.RRULE, MaintenanceScheduleType.ONE_OFF],
    }),
    [selectedTags, tagsMatch]
  );

  useEffect(() => {
    if (skipRequest) {
      return;
    }

    searchSchedules({
      variables: {
        search,
        filters: scheduleFilters,
      },
    }).then(({data}) => {
      const scheduleIDs = (data?.response?.items || []).map((i) => i.id).filter((i) => !(excludeIds || []).includes(i));
      const newAvailableSchedules = scheduleIDs.filter((i) => !selectedSchedules.includes(i));
      setAvailableSchedules(newAvailableSchedules || []);
    });
    // eslint-disable-next-line
  }, [searchValue, scheduleFilters, skipRequest, setAvailableSchedules]);

  const schedules = data?.response?.items || [];

  const getListPanelValues = (filterArray: number[]) =>
    schedules.reduce((acc, schedule) => {
      if (filterArray.includes(schedule.id)) {
        const recurrenceStr = getRecurrenceDisplay(schedule);
        const scheduleState = getScheduleState(schedule);
        const nextScheduledTimeStr = capitalize(getNextOccurrenceText(schedule));
        const isCurrentlyActive = scheduleState === MaintenanceScheduleState.IN_MAINTENANCE;
        const subtible = `${nextScheduledTimeStr} – ${recurrenceStr}`;
        acc.push({
          value: schedule.id,
          title: schedule.name,
          subtitle: subtible,
          tag: "",
          accessory: isCurrentlyActive && (
            <TooltipIconRA
              title={"This Maintenance Schedule is currently Active. "}
              icon={FiTool}
              className="pulse-color-yellow-600"
            />
          ),
        });
      }
      return acc;
    }, [] as ListPanelSelectOption[]);

  return (
    <div className={className}>
      <div className="d-flex gap-3 flex-column">
        <div>
          <FormLabel forId="multi-service-search" helpText="Select the Schedules you would like to link">
            Search Schedules
          </FormLabel>
          <SearchInput onChange={setSearch} id="multi-service-search" />
        </div>
        <div>
          <FormLabel
            forId="multi-service-tags"
            helpText="Filter schedules by one or more tags. Matches schedules with any of the selected tags."
          >
            Tags
          </FormLabel>
          <TagSelect
            id="multi-service-tags"
            isMulti
            options={tags}
            onChange={(value) => setSelectedTags(value ?? [])}
            value={selectedTags}
            placeholder="Select Tags"
            isClearable
          />
          <div className="mt-3">
            <MatchModeToggle value={tagsMatch} onChange={setTagsMatch} />
          </div>
        </div>
      </div>
      <Divider className="my-3" />
      <div className="d-flex gap-3 flex-column flex-sm-row">
        <div className={styles.container}>
          <FormLabel forId="schedules-available" helpText="Click on Schedules to add" className="mb-2">
            Schedules Available
          </FormLabel>
          <div className={classNames(styles.listContainer, styles[variant])}>
            <ListPanelSelect
              id="schedules-available"
              className={classNames(styles.listPanel, styles[variant])}
              noOptionsText={loading ? <LoadingContainer /> : "No Schedules Available"}
              onChange={(items) => {
                const elToAdd = items.find(({value}) => !selectedSchedules.includes(value))!.value;
                setSelectedSchedules([...selectedSchedules, elToAdd]);
                setAvailableSchedules(availableSchedules.filter((c) => c !== elToAdd));
                onAppend(elToAdd);
              }}
              options={getListPanelValues(availableSchedules)}
              value={getListPanelValues(selectedSchedules)}
              hideTag
            />
          </div>
          <Button
            variant="linkBlue"
            size="fitContent"
            typography="linkBold"
            onClick={() => {
              const selected = union(selectedSchedules, availableSchedules);
              setSelectedSchedules(selected);
              setAvailableSchedules([]);
              onReplace(selected);
            }}
          >
            Select All
          </Button>
        </div>
        <div className={styles.container}>
          <FormLabel forId="schedules-selected" helpText="Click on Schedules to remove" className="mb-2">
            {`${pluralize("Schedule", selectedSchedules.length, true)} Selected`}
          </FormLabel>
          <div className={classNames(styles.listContainer, styles[variant])}>
            <ListPanelSelect
              id="schedules-selected"
              className={classNames(styles.listPanel, styles[variant])}
              noOptionsText="No Schedules Selected"
              onChange={([{value}]) => {
                const elToRemove = selectedSchedules.find((el) => el === value)!;
                const newSelectedSchedules = selectedSchedules.filter((c) => c !== elToRemove);
                setSelectedSchedules(newSelectedSchedules);
                onReplace(newSelectedSchedules);
                return refetch({
                  search,
                  filters: scheduleFilters,
                  include: newSelectedSchedules,
                }).then((res) => {
                  const schedulesIDs = (res.data?.response?.items || []).map((i) => i.id);
                  const newAvailableSchedules = schedulesIDs.filter((i) => !newSelectedSchedules.includes(i));
                  setAvailableSchedules(newAvailableSchedules || []);
                });
              }}
              options={getListPanelValues(selectedSchedules)}
              value={[]}
              hideTag
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="linkBlue"
              size="fitContent"
              typography="linkBold"
              onClick={() => {
                const selected = union(selectedSchedules, availableSchedules);
                setAvailableSchedules(selected);
                setSelectedSchedules([]);
                onReplace([]);
              }}
            >
              Remove All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
